/**
 * Catalogue LE 11 — source de vérité unique (Phase 0, admin-ready).
 *
 * Tout est centralisé ici pour être facilement migré vers /api/menu côté
 * MongoDB en Phase 1. Aucun composant ne doit hardcoder un libellé/prix
 * directement.
 *
 * Convention :
 *  - `id`     identifiant stable (slug)
 *  - `name`   nom public
 *  - `price`  string formaté en € (ex. "7,99 €")
 *  - `available` boolean — sera piloté par l'admin plus tard
 */

export const ASSETS = {
  logo: "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/zpxbuuoo_logo%20le%2011.png",
  pizza:
    "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/hkzu7pv0_C40E6AC5-2FFB-4EA2-87C3-ADB8BD356E88.png",
  burgerParatha:
    "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/8smge3kz_E05525FC-EB97-408C-B3E8-BF90D2858319.png",
  naan:
    "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/7wlv07lr_957DB759-F807-4BA9-84D4-D869D98A7841.png",
  merguez:
    "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/emvxi5de_120A022D-14DD-49C3-9F93-1F9E26D8D41C.png",
  tandoor:
    "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/0voiixth_7E1CD008-199C-4866-82B5-423BBEBE4B89.png",
  nemsSamoussas:
    "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/235re4l4_934110B0-C9BA-4F19-B3FD-518EF175BE94.png",
  boissons:
    "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/yby1lle5_5BF77CE3-62C9-4D7B-9BCC-BBD224197800.png",
  desserts:
    "https://customer-assets.emergentagent.com/job_ordering-le11/artifacts/xq8u1haj_300818E0-EDB2-454D-994C-BD7B14106E43.png",
};

/* ---------- Restaurant ---------- */

export const RESTAURANT = {
  name: "LE 11",
  tagline: "Goût • Qualité • Passion",
  domain: "pizza11.fr",
  email: "contact@pizza11.fr",
  phoneDisplay: "01 39 94 19 19",
  phoneTel: "tel:+33139941919",
  address: {
    street: "11 Avenue Alexis Varagne",
    zipCity: "95400 Villiers-le-Bel",
  },
  // Horaires hebdomadaires — pilotable plus tard depuis l'admin
  hours: [
    { days: "Lundi – Jeudi", range: "11h00 – 23h00" },
    { days: "Vendredi – Samedi", range: "11h00 – 03h00" },
    { days: "Dimanche", range: "11h00 – 23h00" },
  ],
  // Mention publique optionnelle (ex. ouverture prochaine)
  openingNotice: null, // ex: "Ouverture prochaine" — passer une string pour afficher
};

/* ---------- Plateformes de livraison ---------- */

export const PLATFORMS = [
  {
    id: "uber-eats",
    name: "Uber Eats",
    color: "#06C167",
    status: "coming-soon", // "hidden" | "coming-soon" | "active"
    url: null,
  },
  {
    id: "deliveroo",
    name: "Deliveroo",
    color: "#00CCBC",
    status: "coming-soon",
    url: null,
  },
];

/* ---------- Réseaux sociaux ---------- */

export const SOCIALS = [
  { id: "facebook", name: "Facebook", icon: "facebook", url: null },
  { id: "instagram", name: "Instagram", icon: "instagram", url: null },
  { id: "tiktok", name: "TikTok", icon: "tiktok", url: null },
  { id: "snapchat", name: "Snapchat", icon: "snapchat", url: null },
];

/* ---------- Halal ---------- */

export const HALAL = {
  title: "Halal • Qualité • Confiance",
  text: "Nos viandes proviennent exclusivement de fournisseurs contrôlés et reconnus dans le secteur halal.",
  organisations: ["AVS", "Achahada", "HMC"],
};

/* ---------- Pizza (héritage Phase 0 — configurateur sur demande en boutique) ---------- */

/**
 * PIZZA est conservé pour /commander (configurateur historique) mais n'est
 * plus mis en avant : le site public affiche désormais PIZZA_SIGNATURES.
 */
export const PIZZA = {
  id: "pizza",
  anchor: "pizza",
  title: "Pizza à composer",
  image: ASSETS.pizza,
  sizes: [{ id: "senior", label: "Senior", price: "7,99 €", numeric: 7.99 }],
  bases: ["Sauce tomate", "Crème fraîche", "Sauce BBQ"],
  cheesesIncluded: [
    "Mozzarella",
    "Cheddar",
    "Fromage râpé",
    "Raclette",
    "Chèvre",
    "Reblochon",
    "Bleu",
  ],
  composition: {
    title: "3 ingrédients inclus",
    combos: [
      "2 viandes + 1 légume",
      "1 viande + 2 légumes",
      "3 légumes",
    ],
  },
  meats: [
    { name: "Jambon dinde" },
    { name: "Jambon poulet" },
    { name: "Poulet fumé" },
    { name: "Lardons" },
    { name: "Poulet mariné" },
    { name: "Pepperoni halal" },
    { name: "Viande hachée" },
    { name: "Saumon", surcharge: "+2,00 €" },
    { name: "Merguez" },
    { name: "Chicken Tikka" },
  ],
  vegetables: [
    "Poivrons",
    "Oignons",
    "Champignons",
    "Maïs",
    "Olives",
    "Tomates fraîches",
    "Pomme de terre",
    "Jalapeños",
  ],
  cheeseExtra: { id: "cheese", label: "Fromage supplémentaire", price: "+1,00 €", numeric: 1.0 },
  supplements: [
    { id: "meat", label: "Viande supplémentaire", price: "+1,50 €", numeric: 1.5 },
    { id: "veg", label: "Légume supplémentaire", price: "+0,80 €", numeric: 0.8 },
    { id: "salmon", label: "Saumon", price: "+2,00 €", numeric: 2.0 },
  ],
};

/* ---------- Pizzas signatures (mise en avant publique) ---------- */

/**
 * Liste officielle validée par le restaurateur — prix unique 10 €.
 * Les noms suivis d'un * correspondent à des viandes halal (cf. mention
 * légale `halalNote`).
 */
export const PIZZA_SIGNATURES = {
  id: "pizza-signatures",
  anchor: "pizza",
  title: "Pizzas signatures",
  unitPrice: "10 €",
  unitPriceNumeric: 10,
  image: ASSETS.pizza,
  groups: [
    {
      id: "tomate",
      title: "Base tomate",
      items: [
        { id: "sig-4saisons", name: "4 Saisons", composition: "Sauce tomate, mozzarella, jambon*, artichaut, champignons, olives." },
        { id: "sig-cheddar", name: "Cheddar", composition: "Sauce tomate, mozzarella, viande hachée*, cheddar, champignons, oignons rouges, olives." },
        { id: "sig-gourmande", name: "Gourmande", composition: "Sauce tomate, mozzarella, pepperoni*, poivrons, cheddar." },
        { id: "sig-newyork", name: "New York", composition: "Sauce tomate, mozzarella, viande hachée*, pepperoni*, champignons, olives." },
        { id: "sig-thai", name: "Thaïlandaise", composition: "Sauce tomate, mozzarella, chicken tandoori*, poivrons, cheddar, champignons." },
        { id: "sig-chicken", name: "Chicken", composition: "Sauce tomate, mozzarella, chicken*, poivrons, oignons, olives." },
        { id: "sig-queen", name: "Queen", composition: "Sauce tomate, mozzarella, jambon*, champignons frais." },
        { id: "sig-jambon-fromage", name: "Jambon Fromage", composition: "Sauce tomate, mozzarella, jambon*." },
        { id: "sig-3jambons", name: "3 Jambons", composition: "Sauce tomate, mozzarella, chorizo*, jambon*, pepperoni*." },
      ],
    },
    {
      id: "creme",
      title: "Base crème fraîche",
      items: [
        { id: "sig-saumon", name: "Saumon", composition: "Crème fraîche, mozzarella, saumon fumé, œuf." },
        { id: "sig-4fromages", name: "4 Fromages", composition: "Crème fraîche, mozzarella, chèvre, gorgonzola, emmental." },
        { id: "sig-chevre-miel", name: "Chèvre Miel", composition: "Crème fraîche, mozzarella, chèvre, miel." },
        { id: "sig-bacon", name: "Bacon", composition: "Crème fraîche, mozzarella, poulet*, oignons, bacon*, sauce barbecue." },
        { id: "sig-boursin", name: "Boursin", composition: "Crème fraîche, mozzarella, poulet fumé*, chicken curry*, oignons, boursin, champignons." },
        { id: "sig-kebab-supreme", name: "Kebab Suprême", composition: "Crème fraîche, mozzarella, kebab*, oignons, pommes de terre, boursin." },
        { id: "sig-chicken-supreme", name: "Chicken Suprême", composition: "Crème fraîche, mozzarella, chicken curry*, champignons, cheddar." },
        { id: "sig-country", name: "Country", composition: "Crème fraîche, mozzarella, chicken curry*, poivrons, lardons*." },
        { id: "sig-bollywood", name: "Bollywood", composition: "Crème fraîche, mozzarella, champignons, chicken tandoori*, oignons." },
        { id: "sig-galina", name: "Galina", composition: "Crème fraîche, mozzarella, champignons, poulet*, pommes de terre." },
        { id: "sig-milano", name: "Milano", composition: "Crème fraîche, mozzarella, viande de veau*, pommes de terre, œuf." },
        { id: "sig-boursin-poulet", name: "Boursin Poulet", composition: "Crème fraîche, mozzarella, poulet*, fromage boursin, pommes de terre, olives." },
        { id: "sig-tartiflette", name: "Tartiflette", composition: "Crème fraîche, mozzarella, lardons*, pommes de terre, reblochon, oignons." },
        { id: "sig-parsanne", name: "Parsanne", composition: "Crème fraîche, mozzarella, pommes de terre, poulet fumé*." },
        { id: "sig-saumon-boursin", name: "Saumon et Boursin", composition: "Crème fraîche, mozzarella, saumon, boursin." },
        { id: "sig-montagnarde", name: "Montagnarde", composition: "Crème fraîche, mozzarella, jambon*, pomme de terre, oignons, olives." },
        { id: "sig-royal", name: "Royal", composition: "Crème fraîche, mozzarella, jambon*, merguez*, viande hachée*." },
      ],
    },
  ],
  halalNote:
    "* Toutes les viandes sont halal. Jambon : poulet ou dinde selon disponibilité.",
  composeHint:
    "Pizza à composer possible sur demande en boutique.",
};

/* ---------- Naan Burger (anciennement Burgers Paratha) ---------- */

export const BURGERS = {
  id: "burgers",
  anchor: "burgers",
  title: "Burger",
  image: ASSETS.burgerParatha,
  items: [
    {
      id: "le11-classic",
      name: "LE 11 Classic",
      price: "7,00 €",
      composition: ["Steak", "Cheddar", "Salade", "Tomate", "Sauce maison"],
    },
    {
      id: "le11-chicken",
      name: "LE 11 Chicken",
      price: "7,50 €",
      chickenChoice: true,
      composition: [
        "Poulet croustillant",
        "Fromage",
        "Salade",
        "Tomate",
        "Sauce maison",
      ],
    },
    {
      id: "le11-double",
      name: "LE 11 Double",
      price: "9,00 €",
      composition: [
        "Double steak",
        "Double fromage",
        "Salade",
        "Tomate",
        "Sauce maison",
      ],
    },
    {
      id: "le11-spicy",
      name: "LE 11 Spicy",
      price: "8,00 €",
      chickenChoice: true,
      composition: [
        "Poulet épicé",
        "Fromage",
        "Salade",
        "Tomate",
        "Sauce piquante",
        "Sauce maison",
      ],
    },
  ],
  options: [
    { id: "menu-supp", label: "Menu (frites + boisson)", price: "+3,00 €" },
  ],
};

/* ---------- Naan Rolls ---------- */

export const NAAN = {
  id: "naan",
  anchor: "naan",
  title: "Naan Rolls",
  image: ASSETS.naan,
  items: [
    {
      id: "chicken-naan",
      name: "Chicken Naan",
      price: "7,00 €",
      chickenChoice: true,
      composition: ["Pain naan", "Poulet", "Salade", "Tomate", "Sauce blanche"],
    },
    {
      id: "beef-naan",
      name: "Beef Naan",
      price: "7,50 €",
      composition: [
        "Pain naan",
        "Steak",
        "Salade",
        "Tomate",
        "Oignons",
        "Sauce burger",
      ],
    },
    {
      id: "naan-extra-cheese",
      name: "Naan Extra Cheese",
      price: "8,00 €",
      chickenChoice: true,
      composition: [
        "Pain naan",
        "Viande au choix : poulet, steak ou poulet épicé",
        "Cheddar",
        "Sauce fromagère",
        "Fromage supplémentaire",
        "Salade",
        "Tomate",
      ],
    },
  ],
  options: [
    { id: "cheese-naan", label: "Option Cheese Naan", price: "+1,50 €" },
    { id: "menu-supp", label: "Menu (frites + boisson)", price: "+3,00 €" },
  ],
};

/**
 * Choix Chicken Curry / Tandoori — applicable aux items chicken
 * (Naan Burger ET Naan Rolls). Aucun supplément.
 */
export const CHICKEN_CHOICES = [
  { id: "curry", label: "Chicken Curry" },
  { id: "tandoori", label: "Chicken Tandoori" },
];

/* ---------- Merguez ---------- */

export const MERGUEZ = {
  id: "merguez",
  anchor: "merguez",
  title: "Sandwich Merguez",
  image: ASSETS.merguez,
  items: [
    {
      id: "merguez-classic",
      name: "Merguez Classic",
      price: "4,50 €",
      composition: [
        "Pain baguette",
        "2 merguez",
        "Salade",
        "Tomate",
        "Sauce au choix",
      ],
    },
    {
      id: "merguez-cheese",
      name: "Merguez Cheese",
      price: "5,50 €",
      composition: [
        "Pain baguette",
        "2 merguez",
        "Fromage",
        "Salade",
        "Tomate",
        "Oignons",
        "Sauce au choix",
      ],
    },
  ],
  options: [
    { id: "menu-supp", label: "Menu (frites + boisson)", price: "+3,00 €" },
  ],
};

/* ---------- Entrées ---------- */

export const ENTREES = {
  id: "entrees",
  anchor: "entrees",
  title: "Entrées",
  subgroups: [
    {
      id: "tandoor",
      title: "Cuit au Tandoor",
      image: ASSETS.tandoor,
      items: [
        { id: "chicken-tikka", name: "Chicken Tikka", desc: "Poulet mariné aux épices, cuit au tandoor", price: "8,90 €" },
        { id: "seekh-kebab", name: "Seekh Kebab", desc: "Viande hachée épicée, cuite au tandoor", price: "8,90 €" },
        { id: "peri-peri", name: "Peri Peri Chicken", desc: "Poulet mariné peri peri, cuit au tandoor", price: "8,90 €" },
      ],
    },
    {
      id: "autres",
      title: "Nems & Samoussas",
      image: ASSETS.nemsSamoussas,
      note: "Selon disponibilité",
      items: [
        {
          id: "nems",
          name: "Nems (x4)",
          desc: "Nems croustillants — portion de 4",
          price: "6,00 €",
          variants: ["Poulet", "Crevette", "Légumes"],
        },
        {
          id: "samoussas",
          name: "Samoussas (x3)",
          desc: "Samoussas croustillants — portion de 3",
          price: "6,00 €",
          variants: ["Viande hachée", "Légumes"],
        },
      ],
    },
  ],
};

/* ---------- Accompagnements ---------- */

export const SIDES = {
  id: "sides",
  anchor: "accompagnements",
  title: "Accompagnements",
  items: [
    { id: "frites", name: "Frites fraîches maison", price: "2,50 €" },
    {
      id: "frites-cheddar",
      name: "Frites fraîches maison Cheddar",
      price: "3,50 €",
      option: { label: "Bacon halal", price: "+1,00 €" },
    },
    { id: "nuggets", name: "Nuggets (x6)", price: "3,50 €" },
  ],
};

/* ---------- Boissons ---------- */

export const DRINKS = {
  id: "drinks",
  anchor: "boissons",
  title: "Boissons",
  image: ASSETS.boissons,
  // Affichage individuel demandé : chaque référence est listée.
  groups: [
    {
      id: "sodas",
      title: "Sodas",
      price: "2,00 €",
      items: [
        { id: "coca", name: "Coca-Cola" },
        { id: "coca-zero", name: "Coca-Cola Zéro" },
        { id: "fanta", name: "Fanta" },
        { id: "orangina", name: "Orangina" },
        { id: "oasis", name: "Oasis" },
        { id: "tropico", name: "Tropico" },
        { id: "sprite", name: "Sprite" },
      ],
    },
    {
      id: "sodas-speciaux",
      title: "Sodas spéciaux",
      price: "3,00 €",
      items: [], // à compléter via l'admin
    },
    {
      id: "eau",
      title: "Eau",
      price: "1,50 €",
      items: [],
    },
    {
      id: "lassi",
      title: "Lassi mangue",
      price: "4,90 €",
      items: [],
    },
  ],
};

/* ---------- Desserts ---------- */

export const DESSERTS = {
  id: "desserts",
  anchor: "desserts",
  title: "Desserts",
  image: ASSETS.desserts,
  note: "Selon disponibilité",
  items: [
    { id: "donut", name: "Donut", price: "2,00 €", available: true },
    { id: "cookie", name: "Cookie", price: "2,00 €", available: true },
    { id: "brownie", name: "Brownie", price: "3,00 €", available: true },
    { id: "tiramisu", name: "Tiramisu", price: "3,00 €", available: true },
    { id: "tarte-daims", name: "Tarte aux Daims", price: "2,90 €", available: true },
  ],
};

/* ---------- Offres ---------- */

/**
 * Schéma d'une offre (admin-ready) :
 *   id, title, description, price, conditions,
 *   media_type ("image" | "pdf" | null), media_url, pdf_url,
 *   active                (master switch)
 *   show_on_offers_page   (affichage page /offres)
 *   popup_enabled         (peut apparaître en popup)
 *   popup_frequency       ("once_per_session" | "once_per_day")
 *   start_date, end_date  (ISO yyyy-mm-dd ou null)
 *   active_days           (array [1..7], 1=lundi … 7=dimanche, [] = tous)
 *   start_time, end_time  ("HH:MM" ou null = pas de restriction)
 *   cta_label, cta_target (cta sur la carte ; cta_target = "/offres#xxx")
 *   composition           (array de bullet points)
 *   drink_hint            (texte discret)
 *   drink_rule            (règle métier interne, non affichée)
 */

export const OFFERS = {
  id: "offers",
  anchor: "offres",
  title: "Offres",
  items: [
    {
      id: "menu-etudiant",
      title: "LE 11 DES ÉTUDIANTS",
      subtitle: "Menu étudiant",
      description: "Pizza seule, 3 ingrédients au choix — formule midi.",
      price: "5,99 €",
      conditions: "Voir conditions en boutique",
      composition: ["Pizza seule", "3 ingrédients au choix"],
      drink_hint: null,
      drink_rule: null,
      media_type: null,
      media_url: null,
      pdf_url: null,
      active: true,
      show_on_offers_page: true,
      popup_enabled: true,
      popup_frequency: "once_per_day",
      start_date: null,
      end_date: null,
      active_days: [1, 2, 3, 4, 5], // lun-ven
      start_time: "11:00",
      end_time: "14:00",
      cta_label: "Voir l'offre",
      cta_target: "/offres#menu-etudiant",
    },
    {
      id: "menu-enfant",
      title: "LE P\u2019TIT 11",
      subtitle: "Menu enfant",
      description:
        "Menu enfant — 4 nuggets ou hamburger ou cheeseburger + frites + Capri-Sun ou eau.",
      price: "4,50 €",
      conditions: null,
      composition: [
        "Au choix : 4 nuggets · Hamburger · Cheeseburger",
        "Frites",
        "Capri-Sun ou eau",
      ],
      drink_hint: null,
      drink_rule: null,
      media_type: null,
      media_url: null,
      pdf_url: null,
      active: true,
      show_on_offers_page: true,
      popup_enabled: false,
      popup_frequency: "once_per_session",
      start_date: null,
      end_date: null,
      active_days: [],
      start_time: null,
      end_time: null,
      cta_label: "Voir l'offre",
      cta_target: "/offres#menu-enfant",
    },
  ],
};

/* ---------- Allergènes (info indicative — à confirmer en boutique) ---------- */

export const ALLERGENS = [
  {
    id: "gluten",
    name: "Gluten",
    desc: "Pains paratha, naan, pizza, panures, samoussas, nems, brownie, cookie, donut, tiramisu.",
  },
  {
    id: "lait",
    name: "Lait",
    desc: "Fromages (mozzarella, cheddar, fromage râpé), crème fraîche, lassi, desserts.",
  },
  {
    id: "oeufs",
    name: "Œufs",
    desc: "Préparations panées, certaines sauces et pâtisseries (brownie, cookie, donut, tiramisu).",
  },
  {
    id: "soja",
    name: "Soja",
    desc: "Présent dans certaines sauces, marinades et préparations.",
  },
  {
    id: "moutarde",
    name: "Moutarde",
    desc: "Sauces et marinades.",
  },
  {
    id: "celeri",
    name: "Céleri",
    desc: "Possiblement présent dans bouillons et préparations industrielles.",
  },
  {
    id: "sesame",
    name: "Sésame",
    desc: "Pains, garnitures, certaines préparations.",
  },
  {
    id: "poisson",
    name: "Poisson",
    desc: "Option saumon en supplément pizza.",
  },
  {
    id: "fruits-a-coque",
    name: "Fruits à coque",
    desc: "Traces possibles dans desserts et préparations.",
  },
  {
    id: "arachides",
    name: "Arachides",
    desc: "Traces possibles dans certaines préparations.",
  },
  {
    id: "sulfites",
    name: "Sulfites",
    desc: "Présents dans certaines boissons et préparations industrielles.",
  },
];

/* ---------- Popup config (peut être surchargé par l'admin via localStorage) ---------- */

export const POPUP_DEFAULTS = {
  enabled: true,
  offer_id: "menu-etudiant", // offre à mettre en avant
  delay_ms: 1500, // délai avant apparition après chargement
};

/* ---------- Mentions communes ---------- */

export const COPY = {
  // Mention uniformisée pour tous les visuels du site
  nonContractual: "Visuel non contractuel",
  halalBadge: "Viandes halal sélectionnées",
};

/* ---------- Catégories principales (ordre d'affichage Menu) ---------- */

export const CATEGORIES = [PIZZA_SIGNATURES, BURGERS, NAAN, MERGUEZ, ENTREES, SIDES, DRINKS, DESSERTS];
